import type { Lang, RoomView } from "@shared/types";
import type { BluffSettings } from "@shared/games/bluff";
import { BLUFF_MAX_LIE } from "@shared/games/bluff";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "../../components/Avatar";
import { Btn } from "../../components/Button";
import { Check, Laugh, Send, Sparkles, Volume2 } from "../../components/Icon";
import { TimerBar } from "../../components/Timer";
import { toast } from "../../components/Toast";
import { useI18n } from "../../i18n";
import { narrator } from "../../lib/narrator";
import type { RoomClient } from "../../lib/room-client";
import { haptic, play } from "../../lib/sfx";
import { AudienceNote, Banner, FinalResults, GameChrome, PlayerStrip, Scoreboard } from "../common/GameKit";
import { useGameAct } from "../common/useRoom";

interface Opt {
  id: string;
  text: string;
  mine: boolean;
  liked: boolean;
}
interface RevealRow {
  id: string;
  text: string;
  kind: "truth" | "player" | "house";
  owners: string[];
  auto: boolean;
  voters: string[];
  points: number;
  likes: number;
}
export interface BluffV {
  phase: "intro" | "write" | "vote" | "reveal" | "scores" | "over";
  deadline: number | null;
  phaseMs: number;
  q: number;
  total: number;
  act: number;
  acts: number[];
  mult: number;
  cat: string;
  qid: string;
  question: string | null;
  answer: string | null;
  isActive: boolean;
  active: string[];
  myLie: string | null;
  myLieAuto: boolean;
  suggestion: string | null;
  helpLeft: number;
  submitted: string[];
  options: Opt[];
  myVote: string | null;
  voted: string[];
  reveal: RevealRow[];
  revealStep: number | null;
  revealTotal: number;
  scores: Record<string, number>;
  delta: Record<string, number>;
  ranking: string[];
  awards: { key: string; id: string; value: number; text?: string }[] | null;
}

export function BluffView({ client, view, tv }: { client: RoomClient; view: RoomView; tv?: boolean }) {
  const v = view.view as BluffV;
  const { d } = useI18n();
  const g = useGameAct(client);
  useNarration(client, view, v);

  const skip = v.phase === "write" || v.phase === "vote" ? () => g({ t: "skip" }) : v.phase === "intro" || v.phase === "reveal" || v.phase === "scores" ? () => g({ t: "next" }) : undefined;
  const skipLabel = v.phase === "write" ? d.bluff.skipWrite : v.phase === "vote" ? d.bluff.skipVote : d.common.next;

  if (v.phase === "over") {
    const rows = Object.entries(v.scores).map(([id, score]) => ({ id, score }));
    return (
      <>
        <GameChrome client={client} view={view} />
        <FinalResults client={client} view={view} rows={rows} title={d.bluff.finalTitle}>
          <Awards view={view} v={v} />
        </FinalResults>
      </>
    );
  }

  return (
    <>
      <GameChrome client={client} view={view} onSkip={skip} skipLabel={skipLabel} />
      <div className="shell space-y-6 pt-2">
        <QuestionHeader v={v} />
        {v.phase === "intro" && <Intro v={v} />}
        {v.phase === "write" && <Write client={client} view={view} v={v} tv={tv} />}
        {v.phase === "vote" && <Vote client={client} view={view} v={v} tv={tv} />}
        {v.phase === "reveal" && <Reveal view={view} v={v} />}
        {v.phase === "scores" && (
          <Scoreboard
            view={view}
            title={d.bluff.scoresTitle}
            moodByRank
            rows={Object.entries(v.scores).map(([id, score]) => ({ id, score, delta: v.delta[id] }))}
          />
        )}
      </div>
    </>
  );
}

function QuestionHeader({ v }: { v: BluffV }) {
  const { d, f } = useI18n();
  const actName = [d.bluff.act0, d.bluff.act1, d.bluff.act2][v.act] ?? d.bluff.act0;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm font-extrabold">
      <span className="rounded-full bg-bluff px-3 py-1 text-white">{actName}{v.mult > 1 ? ` ×${v.mult}` : ""}</span>
      <span className="rounded-full bg-ground-3 px-3 py-1 text-ink-2">{f(d.bluff.question, { n: v.q + 1, total: v.total })}</span>
      <span className="rounded-full bg-ground-3 px-3 py-1 text-ink-2">{(d.cats as Record<string, string>)[v.cat] ?? v.cat}</span>
    </div>
  );
}

function Intro({ v }: { v: BluffV }) {
  const { d } = useI18n();
  const newAct = v.q === 0 || v.acts[v.q - 1] !== v.acts[v.q];
  const title = [d.bluff.act0, d.bluff.act1, d.bluff.act2][v.act];
  const sub = [d.bluff.actSub0, d.bluff.actSub1, d.bluff.actSub2][v.act];
  return newAct ? <Banner title={title} sub={sub} color="var(--color-bluff)" /> : <Banner title={`${v.q + 1} / ${v.total}`} color="var(--color-ink)" />;
}

/** The fact with its blank, as the big centrepiece. */
function Fact({ text, fill, tone }: { text: string; fill?: string | null; tone?: "truth" | "lie" }) {
  const [a, b] = text.split("____");
  return (
    <p className="text-[clamp(24px,4.2vw,44px)] leading-[1.25] font-extrabold">
      {a}
      <span
        className="mx-1 inline-block min-w-[5ch] rounded-xl px-3 text-center align-baseline"
        style={{
          background: fill ? (tone === "lie" ? "var(--color-candy)" : "var(--color-good)") : "var(--color-ground-4)",
          color: fill ? "#170626" : "var(--color-ink-3)",
        }}
      >
        {fill ?? "____"}
      </span>
      {b}
    </p>
  );
}

function Write({ client, view, v, tv }: { client: RoomClient; view: RoomView; v: BluffV; tv?: boolean }) {
  const { d } = useI18n();
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(true);
  const [shake, setShake] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setText("");
    setEditing(true);
  }, [v.q]);
  useEffect(() => {
    if (v.myLie) setEditing(false);
  }, [v.myLie]);
  const activePlayers = view.players.filter((p) => v.active.includes(p.id));

  async function send(lie: string) {
    const t = lie.trim();
    if (!t) return;
    const r = await client.act({ t: "g", a: { t: "lie", text: t } });
    if (!r.ok) {
      const msg = (d.bluff.errors as Record<string, string>)[r.error] ?? (d.err as Record<string, string>)[r.error] ?? d.err.server;
      toast(msg, "bad");
      play("buzz");
      haptic([20, 40, 20]);
      setShake((n) => n + 1);
      return;
    }
    play("pop");
    haptic(12);
    setEditing(false);
  }

  return (
    <div className="space-y-6">
      <div className="surface p-6 sm:p-8">
        <Fact text={v.question ?? ""} />
        <Reread v={v} lang={view.lang} />
      </div>
      <TimerBar
        deadline={v.deadline}
        total={v.phaseMs}
        offset={client.offset()}
        paused={view.paused}
        color="var(--color-bluff)"
        onLowTime={() => {
          // "Ten seconds left!" — only while someone is still writing
          if (v.submitted.length < v.active.length) void narrator.line("hurry", view.lang);
        }}
      />
      {v.isActive && !tv ? (
        editing ? (
          <form
            key={shake}
            className={`space-y-3 ${shake ? "shake" : ""}`}
            onSubmit={(e) => {
              e.preventDefault();
              void send(text);
            }}
          >
            <label className="label" htmlFor="lie">{d.bluff.writeTitle}</label>
            <div className="flex gap-2">
              <input
                id="lie"
                ref={inputRef}
                className="field text-xl"
                value={text}
                maxLength={BLUFF_MAX_LIE}
                placeholder={d.bluff.writePh}
                autoComplete="off"
                autoCorrect="off"
                enterKeyHint="send"
                onChange={(e) => setText(e.target.value)}
              />
              <Btn type="submit" game="bluff" disabled={!text.trim()} aria-label={d.bluff.send} icon={<Send size={20} />}>
                <span className="hidden sm:inline">{d.bluff.send}</span>
              </Btn>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {v.suggestion ? (
                <button type="button" className="chip on" style={{ "--c": "var(--color-flame)" } as React.CSSProperties} onClick={() => void send(v.suggestion!)}>
                  <Sparkles size={16} /> {v.suggestion} · {d.bluff.helpUse}
                </button>
              ) : (
                v.helpLeft > 0 && (
                  <button type="button" className="chip" onClick={() => client.act({ t: "g", a: { t: "help" } })}>
                    <Sparkles size={16} /> {d.bluff.help} · {d.bluff.helpLeft.replace("{n}", String(v.helpLeft))}
                  </button>
                )
              )}
            </div>
          </form>
        ) : (
          <div className="surface-2 flex items-center justify-between gap-4 p-5">
            <div className="min-w-0">
              <p className="text-sm font-bold text-ink-3">{d.bluff.yourLie}</p>
              <p className="truncate text-2xl font-black">{v.myLie}</p>
            </div>
            <Btn variant="soft" size="sm" onClick={() => { setText(v.myLie ?? ""); setEditing(true); setTimeout(() => inputRef.current?.focus(), 30); }}>
              {d.bluff.change}
            </Btn>
          </div>
        )
      ) : (
        !tv && <AudienceNote />
      )}
      <div className="space-y-2 text-center">
        <p className="font-bold text-ink-3">{d.bluff.waitingOthers}</p>
        <PlayerStrip players={activePlayers} done={v.submitted} />
      </div>
    </div>
  );
}

function Vote({ client, view, v, tv }: { client: RoomClient; view: RoomView; v: BluffV; tv?: boolean }) {
  const { d } = useI18n();
  const g = useGameAct(client);
  const activePlayers = view.players.filter((p) => v.active.includes(p.id));
  const myAvatar = view.players.find((p) => p.id === view.me?.id)?.avatar ?? null;
  return (
    <div className="space-y-6">
      <div className="surface p-6 sm:p-8">
        <Fact text={v.question ?? ""} />
        <Reread v={v} lang={view.lang} />
      </div>
      <TimerBar deadline={v.deadline} total={v.phaseMs} offset={client.offset()} paused={view.paused} color="var(--color-bluff)" />
      <div>
        <h2 className="display text-3xl">{d.bluff.voteTitle}</h2>
        <p className="mt-1 text-ink-3">{d.bluff.voteHint}</p>
      </div>
      {v.myLieAuto && v.isActive && <p className="rounded-2xl bg-ground-3 p-4 font-bold text-flame">{d.bluff.autoLie}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        {v.options.map((o, i) => {
          const picked = v.myVote === o.id;
          const canVote = v.isActive && !tv && !v.myVote && !o.mine;
          return (
            <motion.div key={o.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="relative">
              <button
                className="tile w-full px-5 py-5 pr-16 text-left text-xl font-extrabold"
                disabled={!canVote}
                style={{
                  ["--b" as string]: picked
                    ? "var(--color-flame)"
                    : o.mine
                      ? "color-mix(in srgb, var(--color-bluff) 26%, var(--color-ground-2))"
                      : "var(--color-ground-3)",
                  color: picked ? "#2a1200" : undefined,
                  outline: o.mine ? "3px dashed var(--color-bluff)" : undefined,
                  outlineOffset: o.mine ? "-3px" : undefined,
                }}
                onClick={() => {
                  play("tap");
                  haptic(10);
                  void g({ t: "vote", option: o.id });
                }}
              >
                {o.mine && (
                  <span className="mb-2 flex w-fit items-center gap-1.5 rounded-full bg-bluff py-1 pr-3 pl-1 text-sm font-extrabold text-white">
                    {myAvatar !== null && <Avatar index={myAvatar} size={24} disc={false} />}
                    {d.bluff.yours}
                  </span>
                )}
                {o.text}
                {picked && <span className="mt-1 flex items-center gap-1 text-sm font-bold"><Check size={15} /> {d.bluff.picked}</span>}
              </button>
              {!o.mine && !tv && v.isActive && <LikeButton liked={o.liked} onClick={() => g({ t: "like", option: o.id }, true)} />}
            </motion.div>
          );
        })}
      </div>
      <div className="space-y-2 text-center">
        <PlayerStrip players={activePlayers} done={v.voted} />
      </div>
    </div>
  );
}

/** Hear the question again (on any device, even where the host voice is off) — shown only when it can be read. */
function Reread({ v, lang }: { v: BluffV; lang: Lang }) {
  const { d } = useI18n();
  const [can, setCan] = useState(false);
  useEffect(() => {
    let alive = true;
    setCan(false);
    void narrator.canRead(v.qid, lang).then((ok) => alive && setCan(ok));
    return () => {
      alive = false;
    };
  }, [v.qid, lang]);
  if (!v.question || !can) return null;
  const text = v.question;
  return (
    <button
      type="button"
      className="mt-4 inline-flex items-center gap-2 rounded-full bg-ground-3 px-4 py-2 text-sm font-bold text-ink-2 transition-colors hover:bg-ground-4"
      onClick={() => {
        narrator.cancel();
        void narrator.question(v.qid, text, lang, true);
      }}
    >
      <Volume2 size={16} /> {d.bluff.reread}
    </button>
  );
}

function LikeButton({ liked, onClick }: { liked: boolean; onClick(): void }) {
  const { d } = useI18n();
  return (
    <button
      className="absolute top-1/2 right-3 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full transition-transform active:scale-90"
      style={{ background: liked ? "var(--color-flame)" : "var(--color-ground-4)", color: liked ? "#2a1200" : "var(--color-ink-2)" }}
      aria-pressed={liked}
      aria-label={d.bluff.likes}
      onClick={(e) => {
        e.stopPropagation();
        play(liked ? "tap" : "pop");
        onClick();
      }}
    >
      <Laugh size={22} />
    </button>
  );
}

function Reveal({ view, v }: { view: RoomView; v: BluffV }) {
  const { d, f } = useI18n();
  const cur = v.reveal[v.reveal.length - 1];
  const meId = view.me?.id;
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? "?";
  const lastKey = useRef<string>("");
  useEffect(() => {
    if (!cur) return;
    const key = `${v.q}:${cur.id}`;
    if (lastKey.current === key) return;
    lastKey.current = key;
    if (cur.kind === "truth") {
      const found = meId && cur.voters.includes(meId);
      play("drumroll", { intensity: 0.4 });
      setTimeout(() => play(found ? "correct" : "ding"), 1900);
    } else {
      const fooledMe = meId && cur.voters.includes(meId);
      const mine = meId && cur.owners.includes(meId);
      play("reveal");
      setTimeout(() => {
        if (fooledMe) {
          play("fooled");
          haptic([30, 60, 30, 60, 120]);
        } else if (mine) play("coins");
        else play("fooled", { volume: 0.55 });
      }, 900);
    }
  }, [cur, v.q, meId]);
  if (!cur) return null;
  const truth = cur.kind === "truth";
  const owners = cur.owners.map(name);
  return (
    <div className="space-y-6">
      <div className="surface p-6 sm:p-8">
        <Fact text={v.question ?? ""} fill={cur.text} tone={truth ? "truth" : "lie"} />
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={cur.id}
          initial={{ scale: 0.7, opacity: 0, rotate: -3 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 18 }}
          className="rounded-[28px] p-6 text-center sm:p-10"
          style={{ background: truth ? "color-mix(in oklab, var(--color-good) 26%, var(--color-ground-2))" : "color-mix(in oklab, var(--color-candy) 22%, var(--color-ground-2))" }}
        >
          <p className="display text-[clamp(30px,6vw,64px)]">{cur.text}</p>
          <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="mt-3 text-2xl font-black" style={{ color: truth ? "var(--color-good)" : "var(--color-candy)" }}>
            {truth
              ? d.bluff.revealTruth
              : cur.kind === "house"
                ? d.bluff.revealHouse
                : cur.auto
                  ? f(d.bluff.revealAuto, { name: owners.join(", ") })
                  : owners.length > 1
                    ? f(d.bluff.revealLies, { names: owners.join(", ") })
                    : f(d.bluff.revealLie, { name: owners[0] ?? "?" })}
          </motion.p>
          <div className="mt-6">
            <p className="mb-3 font-bold text-ink-2">
              {cur.voters.length === 0 ? (truth ? d.bluff.nobodyFound : "") : truth ? d.bluff.found : d.bluff.fooled}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {cur.voters.map((id, i) => {
                const p = view.players.find((x) => x.id === id);
                if (!p) return null;
                return (
                  <motion.div key={id} initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 + i * 0.15, type: "spring", stiffness: 420, damping: 16 }} className="flex flex-col items-center">
                    <Avatar index={p.avatar} color={p.color} size={72} mood={truth ? "happy" : "sad"} />
                    <span className="mt-1 max-w-[9ch] truncate font-bold">{p.name}</span>
                  </motion.div>
                );
              })}
            </div>
          </div>
          {!truth && cur.points > 0 && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.1, type: "spring" }} className="mt-6 flex items-center justify-center gap-3">
              {cur.owners.map((id) => {
                const p = view.players.find((x) => x.id === id);
                return p ? <Avatar key={id} index={p.avatar} color={p.color} size={60} mood="happy" /> : null;
              })}
              <span className="display text-4xl text-flame">+{cur.points}</span>
            </motion.div>
          )}
          {truth && cur.voters.length > 0 && <p className="display mt-5 text-4xl text-good">+{cur.points}</p>}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function Awards({ view, v }: { view: RoomView; v: BluffV }) {
  const { d, f } = useI18n();
  if (!v.awards?.length) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {v.awards.map((a) => {
        const p = view.players.find((x) => x.id === a.id);
        if (!p) return null;
        const sub = (d.bluff.awardsSub as Record<string, string>)[a.key];
        return (
          <div key={a.key} className="surface flex items-center gap-4 p-4">
            <Avatar index={p.avatar} color={p.color} size={60} mood={a.key === "gullible" ? "sad" : "happy"} />
            <div className="min-w-0">
              <p className="font-black text-flame">{(d.bluff.awards as Record<string, string>)[a.key]}</p>
              <p className="truncate text-lg font-extrabold">{p.name}</p>
              <p className="truncate text-sm text-ink-3">{f(sub, { n: a.value, text: a.text ?? "" })}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Drive the host voice from phase changes (only on the device that narrates). */
/** The last finished game on this device, so a replay's host can say a word about it. */
let lastGame: { winner: string | null; loser: string | null } | null = null;

function shuffled(n: number): number[] {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** 2–3 questions per game (never the first or the final) where the host jokes instead of "write your lie". */
function pickJokes(total: number): Set<number> {
  const pool = shuffled(Math.max(0, total - 2)).map((i) => i + 1);
  return new Set(pool.slice(0, total <= 5 ? 2 : 3));
}

/** The jokes go round in a shuffled order, so a device doesn't repeat one until all have been told. */
let jokeBag: number[] = [];
let jokeBagSize = 0;
function nextJoke(count: number): number {
  if (!jokeBag.length || jokeBagSize !== count) {
    jokeBag = shuffled(count);
    jokeBagSize = count;
  }
  return jokeBag.pop() ?? 0;
}

function summarize(view: RoomView, v: BluffV): { winner: string | null; loser: string | null } {
  const rows = Object.entries(v.scores).sort((a, b) => b[1] - a[1]);
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? null;
  const top = rows[0];
  const bottom = rows[rows.length - 1];
  return {
    winner: top && (rows.length < 2 || rows[1][1] < top[1]) ? name(top[0]) : null,
    loser: rows.length >= 2 && bottom && rows[rows.length - 2][1] > bottom[1] ? name(bottom[0]) : null,
  };
}

function useNarration(client: RoomClient, view: RoomView, v: BluffV) {
  const lang = view.lang;
  const prev = useRef<{ phase: string; q: number; step: number | null } | null>(null);
  const writeDeadline = useRef<number | null>(null); // the latest one (a pause moves it)
  if (v.phase === "write") writeDeadline.current = v.deadline;
  const fooledTotal = useMemo(() => v.reveal.filter((r) => r.kind !== "truth").reduce((n, r) => n + r.voters.length, 0), [v.reveal]);
  const jokesOn = (view.settings as Partial<BluffSettings> | null)?.jokes !== false;
  const jokeAt = useMemo(() => pickJokes(v.total), [view.round, v.total]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const p = prev.current;
    prev.current = { phase: v.phase, q: v.q, step: v.revealStep };
    if (p && p.phase === v.phase && p.q === v.q && p.step === v.revealStep) return;
    // every device: a little "next question" sound, and remember how the game ended
    if (v.phase === "intro" && v.q > 0 && p?.q !== v.q) play("nextq");
    if (v.phase === "over" && p?.phase !== "over") lastGame = summarize(view, v);
    if (!narrator.enabled()) return;
    if (v.phase === "intro") {
      narrator.cancel();
      narrator.prefetchQuestion(v.qid, lang);
      const newAct = v.q === 0 || v.acts[v.q - 1] !== v.acts[v.q];
      if (v.q === 0) {
        if (view.round > 1) {
          // a replay: "Wow, you're back!" + a word about the last game
          void narrator.line("welcomeBack", lang);
          // the winner or the one who came last, by name when it's clear who; a tie gets the version without a name
          const last = lastGame;
          if (last) {
            const kind = last.loser && last.winner ? (Math.random() < 0.5 ? "loser" : "winner") : last.loser ? "loser" : last.winner ? "winner" : Math.random() < 0.5 ? "loser" : "winner";
            void narrator.prevGame(kind, kind === "loser" ? last.loser : last.winner, lang);
          }
        } else void narrator.line("welcome", lang);
      }
      // a new round is announced; any other question gets its number ("Question three!")
      if (newAct) void narrator.line(`act${v.act}` as "act0", lang);
      else void narrator.questionNumber(v.q + 1, lang);
    } else if (v.phase === "write" && p?.phase !== "write") {
      if (v.question) void narrator.question(v.qid, v.question, lang);
      if (jokesOn && jokeAt.has(v.q)) void narrator.line("joke", lang, nextJoke(narrator.count("joke", lang)));
      else void narrator.line("write", lang);
    } else if (v.phase === "vote" && p?.phase !== "vote") {
      narrator.cancel();
      // the writing time ran out (rather than everyone finishing early): "Pens down!"
      const end = writeDeadline.current;
      const timedOut = p?.phase === "write" && end != null && Date.now() + client.offset() >= end - 1000;
      if (timedOut) void narrator.line("timeup", lang);
      void narrator.line("vote", lang);
    } else if (v.phase === "reveal") {
      const cur = v.reveal[v.reveal.length - 1];
      if (!cur) return;
      narrator.cancel();
      if (cur.kind === "truth") void narrator.line(cur.voters.length === 0 ? "nobody" : cur.voters.length === v.active.length ? "everyone" : "truth", lang);
      else if (cur.voters.length >= 3 || fooledTotal >= 4) void narrator.line("fooledMany", lang);
      else void narrator.line("lie", lang);
    } else if (v.phase === "scores" && p?.phase !== "scores") {
      const actEnds = v.q + 1 >= v.total || v.acts[v.q + 1] !== v.acts[v.q];
      if (actEnds) void narrator.line("scores", lang);
    } else if (v.phase === "over" && p?.phase !== "over") {
      void narrator.line("winner", lang);
      void narrator.pause(5000); // let the podium play out before saying goodbye
      void narrator.line("outro", lang);
    }
  }, [v.phase, v.q, v.revealStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pause: the host goes quiet. Resume: if there is still time to write, he reads the question again.
  const wasPaused = useRef(view.paused);
  useEffect(() => {
    const was = wasPaused.current;
    wasPaused.current = view.paused;
    if (view.paused && !was) narrator.cancel();
    else if (!view.paused && was && narrator.enabled() && v.phase === "write" && v.question && v.deadline) {
      const left = v.deadline - (Date.now() + client.offset());
      if (left > 12_000) void narrator.question(v.qid, v.question, lang);
    }
  }, [view.paused]); // eslint-disable-line react-hooks/exhaustive-deps
}
